import { GridHookForm } from 'vnetwork-ui'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useModal } from '@/contexts/modal'
import { useState } from 'react'
import useUploadFile from '@/hooks/useUploadFile'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { renderFileItem } from '@/utils/function'
import { Attachment } from '@/modules/Upload/types'

interface UpdateNegotiationStatusProps {
    negotiationId: string
    onSubmit: (data: any) => Promise<void>
}

interface UpdateStatusFormData {
    to_status: string
    notes?: string
    reason?: string
    attachments: File[]
}

const STATUS_OPTIONS = [
    { value: 'draft', label: 'Bản nháp' },
    { value: 'in_progress', label: 'Đang tiến hành' },
    { value: 'pending_review', label: 'Chờ xem xét' },
    { value: 'waiting_approval', label: 'Chờ phê duyệt' },
    { value: 'approved', label: 'Đã phê duyệt' },
]

const UpdateNegotiationStatus: React.FC<UpdateNegotiationStatusProps> = ({
    onSubmit,
}) => {
    const { t } = useTranslation()
    const { closeModal } = useModal()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.CONTRACT_NEGOTIATION,
    })

    const formControl = useForm<UpdateStatusFormData>({
        defaultValues: {
            to_status: 'in_progress',
            notes: '',
            reason: '',
            attachments: [],
        },
    })

    const handleSubmit = async (values: UpdateStatusFormData) => {
        setIsSubmitting(true)
        try {
            let attachments: Attachment[] = []
            if (values.attachments && values.attachments.length > 0) {
                const attachmentUploadeds = await uploadAsync(
                    values.attachments
                )
                attachments = attachmentUploadeds
                    ?.filter((attachment) => attachment.status === 'fulfilled')
                    .map((attachment) => attachment.value) as Attachment[]
            }

            await onSubmit({
                to_status: values.to_status,
                notes: values.notes,
                reason: values.reason,
                attachments,
            })
            closeModal()
        } catch (error) {
            console.error('Error updating negotiation status:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="max-w-2xl">
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {t('update-negotiation-status')}
                </h2>
                <p className="text-gray-600">
                    {t('update-negotiation-status-description')}
                </p>
            </div>

            <GridHookForm
                formControl={formControl}
                onSubmit={handleSubmit}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: closeModal,
                    },
                    {
                        title: t('update-status'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isSubmitting,
                        disabled: isSubmitting,
                    },
                ]}
                data={[
                    {
                        formItemProps: {
                            name: 'to_status',
                            type: 'select',
                            label: t('new-status'),
                            options: STATUS_OPTIONS,
                        },
                        className: 'col-span-full md:col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'reason',
                            type: 'textfield',
                            label: t('reason-for-change'),
                            placeholder: t('enter-reason-for-change'),
                        },
                        className: 'col-span-full md:col-span-1',
                    },
                    {
                        formItemProps: {
                            name: 'notes',
                            type: 'textarea',
                            label: t('additional-notes'),
                            placeholder: t('enter-additional-notes'),
                        },
                        className: 'col-span-full',
                    },
                    {
                        formItemProps: {
                            name: 'attachments',
                            type: 'files',
                            multiple: true,
                            label: t('attachments'),
                            placeholder: t('select-attachments'),
                            renderFileItem: (file: File) => {
                                return renderFileItem(file, () => {})
                            },
                        },
                        className: 'col-span-full',
                    },
                ]}
            />
        </div>
    )
}

export default UpdateNegotiationStatus
